import type { SupabaseClient } from '../../lib/supabase';
import { portalDealsFrom } from './briefDeals';
import type { BriefDealsPayload } from './briefDeals';
import type { BriefLinkRow, MintedBriefToken } from './briefTokens';

/**
 * The CEO brief, stored and read.
 *
 * Everything here is gated by RLS on ceo_briefs (super admins only). These
 * functions do not check anything themselves: a second gate in the client
 * would be a gate somebody can edit, and the one that matters is the one in
 * the database.
 */

export interface CeoBrief {
  id: string;
  brief_date: string;
  body: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

const BRIEF_COLUMNS = 'id, brief_date, body, created_by, updated_by, created_at, updated_at';

export async function fetchBriefs(client: SupabaseClient, limit = 60): Promise<CeoBrief[]> {
  const { data, error } = await client
    .from('ceo_briefs')
    .select(BRIEF_COLUMNS)
    .order('brief_date', { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchBriefDeals(client: SupabaseClient): Promise<BriefDealsPayload | null> {
  const { data, error } = await client.rpc('read_brief_deals');
  if (error) throw new Error(error.message);
  return data === null ? null : portalDealsFrom(data);
}

/**
 * Save a brief for a day.
 *
 * Upsert on brief_date rather than insert: a corrected brief pasted over an
 * earlier one is an EDIT of that day. An insert would fail the unique
 * constraint and leave somebody staring at a database error for what is the
 * most ordinary thing they will do here.
 */
export async function saveBrief(
  client: SupabaseClient,
  input: { briefDate: string; body: string; actorId: string },
): Promise<void> {
  const { error } = await client.from('ceo_briefs').upsert(
    {
      brief_date: input.briefDate,
      body: input.body,
      created_by: input.actorId,
      updated_by: input.actorId,
    },
    { onConflict: 'brief_date' },
  );
  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteBrief(client: SupabaseClient, id: string): Promise<void> {
  const { error } = await client.from('ceo_briefs').delete().eq('id', id);
  if (error) {
    throw new Error(error.message);
  }
}

export const BRIEF_ERROR_MESSAGES: Record<string, string> = {
  ceo_briefs_body_present: 'Paste the brief before saving.',
  ceo_briefs_brief_date_key: 'There is already a brief for that day. Saving will replace it.',
};

export function briefErrorMessage(message: string): string {
  const known = Object.keys(BRIEF_ERROR_MESSAGES).find((key) => message.includes(key));
  if (known !== undefined) {
    return BRIEF_ERROR_MESSAGES[known] ?? message;
  }
  if (message.includes('row-level security') || message.includes('permission denied')) {
    return 'The CEO brief is limited to super admins.';
  }
  return message === '' ? 'That did not save. Try again.' : message;
}

/**
 * The brief links that exist, newest first from the database.
 *
 * Every one of these three calls is gated inside the function by
 * is_super_admin(). Nothing is checked here on purpose: a second gate in the
 * client is a gate somebody can edit, and list_brief_tokens simply returns
 * nothing to anybody else rather than raising.
 */
export async function fetchBriefLinks(client: SupabaseClient): Promise<BriefLinkRow[]> {
  const { data, error } = await client.rpc('list_brief_tokens');
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

/**
 * Mint one. The returned row carries the token, and this is the only moment
 * it is ever readable, so the caller shows it and does not store it.
 */
export async function mintBriefLink(
  client: SupabaseClient,
  label: string,
): Promise<MintedBriefToken> {
  const { data, error } = await client.rpc('create_brief_token', { p_label: label });
  if (error) {
    throw new Error(error.message);
  }
  if (data === null) {
    throw new Error('The link was not created.');
  }
  return data;
}

/**
 * Kill one, by the six-character tail the list shows.
 *
 * Not by token: the panel does not have one and cannot get one, because
 * list_brief_tokens returns only a tail and a live token is readable exactly
 * once. revoke_brief_token, which matches on the whole thing, is therefore
 * unreachable from any screen. 20270143000100 added the tail door and made it
 * refuse an ambiguous tail rather than guess which live link to kill.
 */
export async function revokeBriefLink(client: SupabaseClient, tail: string): Promise<void> {
  const { error } = await client.rpc('revoke_brief_token_by_tail', { p_tail: tail });
  if (error) {
    throw new Error(error.message);
  }
}

/** What the database says when two live links share a tail. */
export function briefLinkErrorMessage(cause: unknown): string {
  const text = cause instanceof Error ? cause.message : String(cause);
  if (text.includes('brief_token_tail_ambiguous')) {
    return 'Two live links end in the same six characters, so this one was not revoked. Revoke it with its full link instead.';
  }
  if (text.includes('not_authorized_for_briefs')) {
    return 'Brief links are super admin only.';
  }
  return text === '' ? 'That did not work.' : text;
}
